import type { Coordinate } from '@indutrust/shared-model';
import type { MapIndustrialArea } from '../../industrial-area/map-industrial-area.model';
import type { MapRegion } from '../map-region.model';
import uaeRegionsSample from './uae-regions.sample.json';

type SampleCoordinate = {
  latitude: number;
  longitude: number;
};

type SampleIndustrialArea = {
  coordinates: SampleCoordinate[];
  freezone: boolean;
  id: number;
  name: string;
  region_id: number;
  website: string | null;
};

type SampleRegion = {
  country_id: number;
  id: number;
  industrial_areas: SampleIndustrialArea[];
  name: string;
};

/**
 * Builds {@link MapRegion} demo seeds from the adapted UAE sample JSON.
 * All areas are `published` so map polygons render.
 */
export function buildUaeMapRegionsDemo(): MapRegion[] {
  const regions = uaeRegionsSample as SampleRegion[];

  return regions.map((region) => ({
    id: region.id,
    country_id: region.country_id,
    name: region.name,
    industrial_areas: region.industrial_areas.map(
      (area): MapIndustrialArea => ({
        id: area.id,
        name: area.name,
        region_id: area.region_id,
        freezone: area.freezone,
        status: 'published',
        website: area.website ?? '',
        coordinates: area.coordinates.map((c) => ({
          latitude: c.latitude,
          longitude: c.longitude,
        })),
      })
    ),
  }));
}

let catalogById: ReadonlyMap<number, Coordinate[]> | undefined;

/**
 * Digitized UAE industrial-area polygons from the sample catalog, keyed by
 * area id. Used when live `/regions` only has a circular placeholder ring.
 */
export function getCatalogIndustrialAreaCoordinates(
  id: number
): Coordinate[] | undefined {
  catalogById ??= new Map(
    buildUaeMapRegionsDemo().flatMap((region) =>
      region.industrial_areas.map((area): [number, Coordinate[]] => [
        area.id,
        area.coordinates,
      ])
    )
  );
  return catalogById.get(id);
}
